"use client";
// src/lib/sample-data/organizations.ts - Sample organization data
import type { Organization } from '@/lib/types';
import { generateRandomSampleDate } from '@/lib/utils';
import { format, subMonths, startOfMonth, addMonths, addYears } from 'date-fns';
import { SAMPLE_DATA_CONFIG } from './config';

const now = new Date();
export const defaultOrganizationId = SAMPLE_DATA_CONFIG.defaultOrganizationId;

// Firebase Tenant Mapping
// Maps organization IDs to their corresponding Firebase tenant IDs
export const FIREBASE_TENANT_MAPPING = {
  [defaultOrganizationId]: 'courtly-sampledata-m1g99', // Main sample tenant
  '2951738460': 'courtly-sampledata-m1g99', // Share same tenant for sample data
  '6174829350': 'courtly-sampledata-m1g99', // Share same tenant for sample data  
  '6666666666': 'courtly-sampledata-m1g99', // Share same tenant for sample data
  '9516274830': 'courtly-sampledata-m1g99', // Share same tenant for sample data
} as const;

// Helper function to get Firebase tenant ID for an organization
export const getFirebaseTenantId = (organizationId: string): string => {
  return FIREBASE_TENANT_MAPPING[organizationId as keyof typeof FIREBASE_TENANT_MAPPING] || 'courtly-sampledata-m1g99';
};

// Helper function to get Firebase tenant ID from organization object
export const getFirebaseTenantIdFromOrg = (organization: Organization): string => {
  return organization.firebaseTenantId || getFirebaseTenantId(organization.id);
};

// Helper function to calculate next billing date based on plan and subscription start date
const calculateNextBillingDate = (subscriptionStartDate: Date, planId: string): string => {
  // Determine billing cycle based on plan
  if (planId === 'plan_starter') {
    // Monthly billing for starter plan
    return format(addMonths(subscriptionStartDate, 1), 'yyyy-MM-dd');
  } else {
    // Yearly billing for advanced and enterprise plans
    return format(addYears(subscriptionStartDate, 1), 'yyyy-MM-dd');
  }
};

export const sampleOrganizationDataForAccountPage: Organization = {
  id: defaultOrganizationId,
  tenantId: defaultOrganizationId, // Internal tenant isolation
  firebaseTenantId: getFirebaseTenantId(defaultOrganizationId), // Firebase tenant ID
  name: 'Courtly Sports Inc.',
  email: 'contact@courtly.com',
  phone: '+41 99 888 77 66',
  address: {
    addressLine1: "123 Sports Avenue",
    city: "Sierre",
    postalCode: "3960",
    stateOrProvince: "Valais",
    country: "Switzerland"
  },
  status: 'Active',
  dateCreated: format(new Date(now.getFullYear() - 2, 0, 1), 'yyyy-MM-dd HH:mm:ss'),
  dateUpdated: format(now, 'yyyy-MM-dd HH:mm:ss'),
      logoUrl: '/user.png',
  website: 'www.courtly.com',
  numberOfCourts: 11,
  foundingYear: 2022,
  description: "The main organization running the Courtly platform and providing services to various clubs. This is the default organization for newly created entities in the admin panel.",
  platformPlanId: 'plan_advanced',
  platformPlanSubscriptionStartDate: format(startOfMonth(subMonths(now, 2)), 'yyyy-MM-dd'),
  nextBillingDate: calculateNextBillingDate(startOfMonth(subMonths(now, 2)), 'plan_advanced'),
  currencyCode: 'CHF',
};

export const sampleOrganisationsDataForSuperAdmin: Organization[] = [
  { ...sampleOrganizationDataForAccountPage },
  {
    id: '2951738460',
    tenantId: '2951738460', // Internal tenant isolation
    firebaseTenantId: getFirebaseTenantId('2951738460'), // Firebase tenant ID
    name: 'Global Padel Group',
    email: 'info@globalpadel.com',
    phone: '+44 20 7123 4567',
    address: { 
      addressLine1: "Padel House, 10 Downing St", 
      city: "London", 
      country: "United Kingdom", 
      postalCode: "SW1A 2AA" 
    },
    status: 'Active',
    dateCreated: format(generateRandomSampleDate(new Date(now.getFullYear() - 3, 0, 1), now), 'yyyy-MM-dd HH:mm:ss'),
    dateUpdated: format(generateRandomSampleDate(new Date(now.getFullYear() - 1, 6, 1), now), 'yyyy-MM-dd HH:mm:ss'),
    logoUrl: '/user.png',
    website: 'www.globalpadel.com',
    numberOfCourts: 50,
    foundingYear: 2020,
    description: "Leading padel facility operator in the UK with multiple locations across London and surrounding areas.",
    platformPlanId: 'plan_enterprise',
    platformPlanSubscriptionStartDate: format(subMonths(now, 6), 'yyyy-MM-dd'),
    nextBillingDate: calculateNextBillingDate(subMonths(now, 6), 'plan_enterprise'),
    currencyCode: 'GBP',
  },
  {
    id: '6174829350',
    tenantId: '6174829350', // Internal tenant isolation
    firebaseTenantId: getFirebaseTenantId('6174829350'), // Firebase tenant ID
    name: 'Urban Tennis Centers',
    email: 'bookings@urbantennis.org',
    phone: '+1 212 555 0199',
    address: { 
      addressLine1: "5th Avenue Courts", 
      city: "New York", 
      stateOrProvince: "NY", 
      country: "USA", 
      postalCode: "10001" 
    },
    status: 'Active',
    dateCreated: format(generateRandomSampleDate(new Date(now.getFullYear() - 1, 0, 1), now), 'yyyy-MM-dd HH:mm:ss'),
    dateUpdated: format(generateRandomSampleDate(new Date(now.getFullYear() - 0.5, 0, 1), now), 'yyyy-MM-dd HH:mm:ss'),
    logoUrl: '/user.png',
    website: 'www.urbantennis.org',
    numberOfCourts: 25,
    foundingYear: 2023,
    description: "Modern tennis facilities in the heart of Manhattan, offering premium court rentals and coaching services.",
    platformPlanId: 'plan_starter',
    platformPlanSubscriptionStartDate: format(subMonths(now, 2), 'yyyy-MM-dd'),
    nextBillingDate: calculateNextBillingDate(subMonths(now, 2), 'plan_starter'),
    currencyCode: 'USD',
  },
  {
    id: '6666666666',
    tenantId: '6666666666', // Internal tenant isolation
    firebaseTenantId: getFirebaseTenantId('6666666666'), // Firebase tenant ID
    name: 'TC Valais Wallis',
    email: 'info@tcvalaiswallis.ch',
    phone: '+41500443322',
    address: { 
      addressLine1: "Route de la Forêt 12", 
      city: "Sierre", 
      postalCode: "3960", 
      stateOrProvince: "Valais", 
      country: "Switzerland" 
    },
    status: 'Inactive',
    dateCreated: format(generateRandomSampleDate(new Date(now.getFullYear() - 5, 0, 1), new Date(now.getFullYear() - 4, 0, 1)), 'yyyy-MM-dd HH:mm:ss'),
    dateUpdated: format(generateRandomSampleDate(new Date(now.getFullYear() - 2, 0, 1), new Date(now.getFullYear() - 1, 0, 1)), 'yyyy-MM-dd HH:mm:ss'),
    logoUrl: '/user.png',
    website: 'www.tcvalaiswallis.ch',
    numberOfCourts: 15,
    foundingYear: 2018,
    description: "Traditional tennis club in the Swiss Alps, known for its scenic courts and professional tournaments.",
    platformPlanId: 'plan_advanced',
    platformPlanSubscriptionStartDate: format(subMonths(now, 12), 'yyyy-MM-dd'),
    nextBillingDate: calculateNextBillingDate(subMonths(now, 12), 'plan_advanced'),
    currencyCode: 'CHF',
  },
  {
    id: '9516274830',
    tenantId: '9516274830', // Internal tenant isolation
    firebaseTenantId: getFirebaseTenantId('9516274830'), // Firebase tenant ID
    name: 'Lagardère Paris Racing',
    email: 'contact@lagardereparisracing.com',
    phone: '+33 1 23 45 67 89',
    address: {
      addressLine1: "1 Chem. de la Croix Catelan",
      city: "Paris",
      postalCode: "75016",
      stateOrProvince: "Île-de-France",
      country: "France"
    },
    status: 'Active',
    dateCreated: format(generateRandomSampleDate(new Date(now.getFullYear() - 10, 0, 1), now), 'yyyy-MM-dd HH:mm:ss'),
    dateUpdated: format(now, 'yyyy-MM-dd HH:mm:ss'),
    logoUrl: '/user.png',
    website: 'www.lagardereparisracing.com',
    numberOfCourts: 43,
    foundingYear: 2014,
    description: "Un club mythique au cœur de Paris, dans un écrin de verdure de 7 hectares. Institution prestigieuse du tennis français.",
    platformPlanId: 'plan_enterprise',
    platformPlanSubscriptionStartDate: format(subMonths(now, 3), 'yyyy-MM-dd'),
    nextBillingDate: calculateNextBillingDate(subMonths(now, 3), 'plan_enterprise'),
    currencyCode: 'EUR',
  }
];
